// WHAT: React hook for fetching and managing Yazio nutrition and activity goals
// WHY:  Provides state management and API integration for retrieving daily goals (calories, macros, steps, etc.)
// DOMAIN: Health → Yazio → Goal Management
// DEPENDENCIES: Tauri core API, React hooks, Yazio backend service
// RISKS:
//   - NOTE: No auto-load on mount - requires manual trigger which may cause UX confusion
//   - NOTE: Error messages are generic - may not help debug specific API failures
//   - WARN: Date parameter defaults to today but format validation is minimal
//   - NOTE: Goal data structure assumes all fields are present - may fail with incomplete API responses

import { core } from "@tauri-apps/api";
import { useState } from "react";

export interface YazioGoalsRequest {
  date?: string; // ISO date string, defaults to today
}

export interface YazioGoalsResponse {
  carbGoal: number;
  date: string;
  energyGoal: number;
  fatGoal: number;
  proteinGoal: number;
  stepsGoal: number;
  waterGoal: number;
  weightGoal: number;
}

interface YazioGoalsState {
  data: undefined | YazioGoalsResponse;
  error: string | undefined;
  isLoading: boolean;
  lastUpdated: Date | undefined;
}

/**
 * WHAT:
 *   React hook for fetching and managing Yazio nutrition and activity goals.
 *
 * WHY:
 *   Provides state management and API integration for retrieving daily goals (calories, macros, steps, etc.).
 *   Allows manual control over data loading to prevent errors when credentials are not configured.
 *
 * @returns Object containing:
 *   - data: YazioGoalsResponse | undefined — Goals data
 *   - error: string | undefined — Error message if fetch failed
 *   - isLoading: boolean — Loading state
 *   - lastUpdated: Date | undefined — Timestamp of last successful fetch
 *   - clearError: () => void — Clear error state
 *   - fetchGoals: (request?: YazioGoalsRequest) => Promise<void> — Fetch goals for specific date
 *   - loadDateData: (date: string) => void — Load goals for specific date
 *   - loadTodayData: () => void — Load today's goals
 *   - refreshData: () => void — Refresh current goals data
 *
 */
export function useYazioGoals() {
  const [state, setState] = useState<YazioGoalsState>({
    data: undefined,
    error: undefined,
    isLoading: false,
    lastUpdated: undefined,
  });

  const fetchGoals = async (request: YazioGoalsRequest = {}) => {
    try {
      setState((previous) => ({ ...previous, error: undefined, isLoading: true }));

      const data = await core.invoke<YazioGoalsResponse>("get_yazio_goals", {
        date: request.date,
      });

      setState((previous) => ({
        ...previous,
        data,
        isLoading: false,
        lastUpdated: new Date(),
      }));
    } catch {
      // NOTE: Failed to fetch goals
      setState((previous) => ({
        ...previous,
        error: "Failed to load goals data",
        isLoading: false,
      }));
    }
  };

  const refreshData = () => {
    if (state.data) {
      void fetchGoals({ date: state.data.date });
    } else {
      void fetchGoals();
    }
  };

  const loadTodayData = () => {
    void fetchGoals();
  };

  const loadDateData = (date: string) => {
    void fetchGoals({ date });
  };

  const clearError = () => {
    setState((previous) => ({ ...previous, error: undefined }));
  };

  // Don't auto-load on mount - let user manually trigger data loading
  // This prevents errors when credentials are not configured

  return {
    ...state,
    clearError,
    fetchGoals,
    loadDateData,
    loadTodayData,
    refreshData,
  };
}
